// @flow
import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { dynamicClassName } from 'utils/dynamicClassName';
import { convertQueryStringToObject, convertObjectToQueryString } from 'utils/urlUtils';
import CardJob from 'components/fragments/card/CardJob';
import Pagination from 'components/fragments/Pagination';

import type { JobListType } from 'types/JobList';
import type { JobFiltersType, JobFiltersSearchType } from 'types/JobFilter';
import { nullJobFiltersSearch } from 'types/JobFilter';
import type { Status } from 'types/Status';

import { STATUS_LOADING } from 'constants/statusConstants';
import Loading from 'components/fragments/Loading';
import JobFilter from 'components/job/JobFilter';

export type StateProps = {
  jobs: Array<JobListType>,
  jobFilters: JobFiltersType,
  maxPage: number,
  totalJobs: number,
  status: Status
};

export type DispatchProps = {
  fetchJobListPreview: (filter: JobFiltersSearchType) => void,
  initialFetchJobListPreview: (filter: JobFiltersSearchType) => void
};

type Props = {
  history: any,
  location: any
} & DispatchProps &
  StateProps;

type State = {
  page: number
};

class JobList extends Component<Props, State> {
  state: State = {
    page: 1
  };
  unlisten: Function;

  componentDidMount() {
    const { initialFetchJobListPreview, history } = this.props;
    const filters: Object = this.getFiltersOnQuery();
    if (filters.page) {
      this.setState({ page: parseInt(filters.page[0], 10) });
    }

    this.unlisten = history.listen(this._onRouteChange);
    initialFetchJobListPreview(filters);
  }

  componentWillUnmount() {
    this.unlisten();
  }

  _onRouteChange = (location: Object, action: string) => {
    const { search } = location;
    if (search !== '') {
      const filters: Object = convertQueryStringToObject(search);
      const { page } = filters;
      let currentPage: number = 1;
      if (page && page[0]) {
        currentPage = parseInt(page[0], 10);
      }

      if (currentPage !== this.state.page && action !== 'REPLACE') {
        this.setState({ page: currentPage }, () => this._onChange());
      }
    }
  };

  getFiltersOnQuery = () => {
    const {
      location: { search }
    } = this.props;
    if (search !== '') {
      return convertQueryStringToObject(search);
    } else {
      return nullJobFiltersSearch;
    }
  };

  _onFilterChange = (filters: Object) => {
    const { history, fetchJobListPreview } = this.props;
    const newFilters = { ...filters, page: 1 };
    this.setState({ page: 1 });
    history.replace({
      search: convertObjectToQueryString(newFilters)
    });

    fetchJobListPreview(newFilters);
  };

  renderCards = () => {
    const { jobs, status } = this.props;

    const restJobs = jobs.length % 3;

    if (status === STATUS_LOADING) {
      return <Loading isWhite />;
    }

    return jobs.map((job, index) => {
      const isOnLastLine =
        (restJobs === 0 && index >= jobs.length - 3) || (restJobs !== 0 && index >= jobs.length - restJobs);

      const classNameContainer = dynamicClassName('col col-md-6 col-lg-4 mb-2 mb-lg-3');
      isOnLastLine ? classNameContainer.add('mb-lg-0') : classNameContainer.add('mb-lg-4');

      return (
        <div key={index} className={classNameContainer.build()}>
          <CardJob
            link={`${job.link}`}
            title={job.title}
            city={job.city}
            club={job.structure.name}
            embleme={job.structure.embleme}
            type_contrat={job.type_contrat}
            jobtype={job.jobtype}
          />
        </div>
      );
    });
  };

  _isLoading = () => {
    const { jobFilters, status } = this.props;

    if (
      jobFilters.domaines.length === 0 &&
      jobFilters.regions.length === 0 &&
      jobFilters.typesContrats.length === 0 &&
      status === STATUS_LOADING
    ) {
      return true;
    }

    return false;
  };

  _getPaginationOption = () => {
    const {
      maxPage,
      location: { search, pathname }
    } = this.props;
    
    const resultOption = [];
    let queryString = '?';

    if (search !== '') {
      const query = convertQueryStringToObject(search);

      if (query.page) {
        delete query.page;
        queryString = convertObjectToQueryString(query);
        queryString = queryString !== '?' ? `${queryString}&` : queryString;
      } else {
        queryString = `${search}&`;
      }
    }

    for (let pageIndex = 1; pageIndex <= maxPage; pageIndex++) {
      resultOption.push({
        number: pageIndex,
        url: `${pathname}${queryString}page=${pageIndex}`
      });
    }

    return resultOption;
  };

  _onChange = () => {
    const { fetchJobListPreview } = this.props;
    const filters = this.getFiltersOnQuery();

    fetchJobListPreview(filters);
  };

  render() {
    const { jobFilters, maxPage, totalJobs, jobs } = this.props;
    const { page } = this.state;

    if (this._isLoading()) {
      return <Loading isWhite />;
    }

    return (
      <Fragment>
        <section>
          <JobFilter
            jobFilters={jobFilters}
            initialFilters={this.getFiltersOnQuery()}
            onFilterChange={filters => this._onFilterChange(filters)}
          />
          <div className="header-filter__sep mb-5" />
          <div className="row">
            <div className="col col-lg-8on9">
              <p className="ft-h6 ft-up ft-black-secondary ft-right">{`${totalJobs} offre(s) disponible(s)`}</p>
              <h2 className="ft-h3 mb-3">Toutes nos offres</h2>
            </div>
          </div>
          { jobs && jobs.length > 0 &&
          <div className="row">
            <div className="col col-lg-8on9">
              <div className="row">{this.renderCards()}</div>
            </div>
          </div> }
          { (!jobs || jobs.length === 0) &&
            <div className="placeholder col col-lg-8on9">
              <p className="ft-h5">
                <i className="icon icon-articles-empty"></i>
                <br/>
                Aucune offre correspondante trouvée.
              </p>
            </div> }
          {parseInt(maxPage, 10) > 1 && page && (
            <Pagination pages={this._getPaginationOption()} currentPage={page} lastPage={parseInt(maxPage, 10)} />
          )}
        </section>
      </Fragment>
    );
  }
}

export default withRouter(JobList);
